import React, { useState, useEffect, useCallback } from 'react';
import { Twitter, RefreshCw, TrendingUp, TrendingDown, Minus, MessageSquare, Heart, Repeat2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_TWITTER_API_URL || '';
const apiConfigured = Boolean(API_BASE);

const TwitterFeed = () => {
  const [tweets, setTweets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchTweets = useCallback(async () => {
    if (!apiConfigured) {
      setLoading(false);
      setTweets([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/tweets`);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Filter tweets by selected user
      const filteredTweets = selectedUser 
        ? data.filter(tweet => tweet.username === selectedUser)
        : data;
      
      setTweets(filteredTweets);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching tweets:', err);
      // Set empty array on error
      setTweets([]);
    } finally {
      setLoading(false);
    }
  }, [selectedUser]);

  const fetchStats = useCallback(async () => {
    if (!apiConfigured) {
      setStats({
        total_tweets: 0,
        unique_users: 0,
        accounts_monitored: 0,
      });
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/stats`);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStats({
        total_tweets: 0,
        unique_users: 0,
        accounts_monitored: 0
      });
    }
  }, []);

  const refreshTweets = async () => {
    if (!apiConfigured) return;
    setRefreshing(true);
    try {
      const response = await fetch(`${API_BASE}/refresh`);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      await response.json();
      
      // Refetch tweets after refresh
      await fetchTweets();
      await fetchStats();
    } catch (err) {
      setError(err.message);
      console.error('Error refreshing tweets:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTweets();
    fetchStats();
  }, [fetchTweets, fetchStats]);

  const getSentimentIcon = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'bearish': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-500" />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish': return 'text-green-600 bg-green-50 border-green-200';
      case 'bearish': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const uniqueUsers = [...new Set(tweets.map(t => t.username))];

  if (!apiConfigured) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <Twitter className="w-10 h-10 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Twitter feed unavailable</h1>
          <p className="text-gray-600">
            Set <code className="text-sm bg-gray-100 px-1 rounded">REACT_APP_TWITTER_API_URL</code> to connect the monitoring service.
          </p>
        </div>
      </div>
    );
  }

  if (loading && tweets.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Twitter className="w-8 h-8 text-blue-500 mr-3" />
          Twitter Feed
        </h1>
        <button
          onClick={refreshTweets}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="card">
          <div className="text-2xl font-bold text-blue-600">
            {stats?.total_tweets || 'N/A'}
          </div>
          <div className="text-gray-600">Total Tweets</div>
        </div>
        <div className="card">
          <div className="text-2xl font-bold text-green-600">
            {stats?.unique_users || 'N/A'}
          </div>
          <div className="text-gray-600">Unique Users</div>
        </div>
        <div className="card">
          <div className="text-2xl font-bold text-purple-600">
            {stats?.accounts_monitored || 'N/A'}
          </div>
          <div className="text-gray-600">Accounts Monitored</div>
        </div>
      </div>

      {/* User Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by User:
        </label>
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All Users</option>
          {uniqueUsers.map(user => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Tweets */}
      {tweets.length === 0 ? (
        <div className="text-center py-12">
          <Twitter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tweets Available</h3>
          <p className="text-gray-500 mb-4">
            {error ? 'Unable to fetch tweets from the API.' : 'No tweets found for the selected criteria.'}
          </p>
          <button
            onClick={refreshTweets}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Refreshing
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {tweets.map((tweet) => (
            <div key={tweet.id} className="card">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Twitter className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">@{tweet.username}</div>
                    <div className="text-sm text-gray-500">
                      {tweet.created_at ? new Date(tweet.created_at).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getSentimentColor(tweet.sentiment)}`}>
                  <div className="flex items-center">
                    {getSentimentIcon(tweet.sentiment)}
                    <span className="ml-1">{tweet.sentiment || 'N/A'}</span>
                  </div>
                </div>
              </div>
              
              <div className="text-gray-800 mb-4">
                {tweet.text || 'N/A'}
              </div>
              
              {tweet.summary && (
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-1">AI Summary:</div>
                  <div className="text-sm text-gray-600">{tweet.summary}</div>
                </div>
              )}
              
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <Heart className="w-4 h-4 mr-1" />
                  {tweet.likes || 'N/A'}
                </div>
                <div className="flex items-center">
                  <Repeat2 className="w-4 h-4 mr-1" />
                  {tweet.retweets || 'N/A'}
                </div>
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Replies
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TwitterFeed;
